import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  useTheme,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  AddCircle as AddIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";

const CropHubAddUpdate = ({ open, onClose, onRefresh }) => {
  const theme = useTheme();
  const toast = useToast();

  const [formData, setFormData] = useState({
    country_ids: [],
    sequence: "",
    common_name: "",
    scientific_name: "",
    family: "",
    image: "",
    climate_requirement: "",
    temperature_requirement: "",
    rainfall_requirement: "",
    soil_type: "",
    soil_ph_range: "",
    seed_rate_kg_per_ha: "",
    variety_name: "",
    sowing_time: "",
    seed_treatment_details: "",
    sowing_depth: "",
    land_preparation_methods: "",
    fertilizer_nutrient_requirement: "",
    irrigation: "",
    harvesting_time: "",
    average_yield_tons_per_ha: "",
  });
  

  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ✅ Fetch countries when modal opens
  useEffect(() => {
    if (open) fetchCountries();
  }, [open]);

  const fetchCountries = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("user_id");

      if (!userId) {
        setError("User ID is missing. Please log in again.");
        setLoading(false);
        return;
      }

      const endpoint = `${API_ENDPOINTS.GET_COUNTRIES_FOR_CROP_GUIDE}?user_id=${userId}`;
      const response = await apiService.get(endpoint);

      if (response?.success && Array.isArray(response?.data)) {
        setCountries(response.data);
      } else if (Array.isArray(response)) {
        setCountries(response);
      } else {
        setCountries([]);
      }
    } catch (err) {
      console.error("Error fetching countries:", err);
      setError("Failed to fetch countries. Please try again.");
      setCountries([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle field change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Handle country multi-select
  const handleCountryChange = (e) => {
    setFormData((prev) => ({ ...prev, country_ids: e.target.value }));
  };

  // ✅ Submit
//   const handleSubmit = async () => {
//     setError("");
//     setSuccess("");
//     setSaving(true);

//     try {
//       const response = await apiService.post(API_ENDPOINTS.CREATE_CROP_GUIDE, formData);

//       if (response?.success) {
//         setSuccess("Crop added successfully!");
//         setTimeout(() => {
//           onClose();
//           onRefresh?.();
//         }, 1500);
//       } else {
//         setError("Failed to add crop. Please check your inputs.");
//       }
//     } catch (err) {
//       console.error("API error:", err);
//       setError(err.message || "Failed to add crop.");
//     } finally {
//       setSaving(false);
//     }
//   };

// ✅ Submit using FormData
const handleSubmit = async () => {
  setError("");
  setSuccess("");
  setSaving(true);

  try {
    const formPayload = new FormData();

    // Append all form fields
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "image" && value instanceof File) {
        formPayload.append(key, value); // append file
      } else {
        formPayload.append(key, value);
      }
    });

    const response = await apiService.post(API_ENDPOINTS.CREATE_CROP_GUIDE, formPayload, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    if (response?.success) {
      const message = "Crop added successfully!";
      setSuccess(message);
      toast.showSuccess(message);
      setTimeout(() => {
        onClose();
        onRefresh?.();
      }, 1500);
    } else {
      const errorMessage = "Failed to add crop. Please check your inputs.";
      setError(errorMessage);
      toast.showError(errorMessage);
    }
  } catch (err) {
    console.error("API error:", err);
    const errorMessage = err.message || "Failed to add crop.";
    setError(errorMessage);
    toast.showError(errorMessage);
  } finally {
    setSaving(false);
  }
};


  return (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth >
  {/* === Header === */}
  <DialogTitle
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      pr: 2,
      
    }}
  >
    <Typography variant="h6" fontWeight={600}>
      Add New Crop
    </Typography>
    <IconButton onClick={onClose}>
      <CloseIcon />
    </IconButton>
  </DialogTitle>

  {/* === Content === */}
  <DialogContent
    dividers
    sx={{
      maxHeight: 600,
      overflowY: "auto",
      /* Firefox */
      scrollbarWidth: "thin",
      scrollbarColor:
        theme.palette.mode === "dark"
          ? "#555 #1e1e1e"
          : "#c1c1c1 #f1f1f1",
      /* Chrome, Edge, Safari */
      "&::-webkit-scrollbar": {
        width: 8,
      },
      "&::-webkit-scrollbar-track": {
        background:
          theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
        borderRadius: 8,
      },
      "&::-webkit-scrollbar-thumb": {
        background:
          theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
        borderRadius: 8,
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background:
          theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
      },
    }}
  >
    {/* ✅ Status Alerts */}
    {error && (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )}
    {success && (
      <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
        {success}
      </Alert>
    )}

    <Grid container spacing={2}>
      {/* 🌍 Countries */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Location
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Countries</InputLabel>
          <Select
            multiple
            value={formData.country_ids}
            onChange={handleCountryChange}
            input={<OutlinedInput label="Countries" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => {
                  const country = countries.find((c) => c.id === value);
                  return (
                    <Chip
                      key={value}
                      label={country?.name || value}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            )}
          >
            {loading ? (
              <MenuItem disabled>Loading...</MenuItem>
            ) : countries.length > 0 ? (
              countries.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  {country.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No countries found</MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>

      {/* 🌾 Crop Information */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Crop Information
        </Typography>
        <Divider sx={{ mb: 1 }} />
      </Grid>

      {[
        { label: "Common Name", name: "common_name" },
        { label: "Scientific Name", name: "scientific_name" },
        { label: "Family", name: "family" },
        { label: "Sequence", name: "sequence", type: "number" },
      ].map((field) => (
        <Grid item xs={12} md={6} key={field.name}>
          <TextField
            label={field.label}
            name={field.name}
            type={field.type || "text"}
            value={formData[field.name]}
            onChange={handleChange}
            fullWidth
          />
        </Grid>
      ))}

      {/* 🌦️ Growing Conditions */}
      <Grid item xs={12} sx={{ mt: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Growing Conditions
        </Typography>
        <Divider sx={{ mb: 1 }} />
      </Grid>

      {[
        { label: "Climate Requirement", name: "climate_requirement" },
        { label: "Temperature Requirement", name: "temperature_requirement" },
        { label: "Rainfall Requirement...", name: "rainfall_requirement" },
        { label: "Soil Type", name: "soil_type" },
        { label: "Soil pH Range", name: "soil_ph_range" },
      ].map((field) => (
        <Grid item xs={12} md={6} key={field.name}>
          <TextField
            label={field.label}
            name={field.name}
            value={formData[field.name]}
            onChange={handleChange}
            fullWidth
            multiline={field.name.includes("requirement")}
            minRows={field.name.includes("requirement") ? 2 : 1}
          />
        </Grid>
      ))}

      {/* 🌱 Cultivation Details */}
      <Grid item xs={12} sx={{ mt: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Cultivation Details
        </Typography>
        <Divider sx={{ mb: 1 }} />
      </Grid>

      {[
        { label: "Seed Rate / Plantlets Rate ", name: "seed_rate_kg_per_ha" },
        { label: "Variety Name", name: "variety_name" },
        { label: "Sowing Time", name: "sowing_time" },
        { label: "Seed Treatment Details", name: "seed_treatment_details" },
        { label: "Sowing Depth", name: "sowing_depth" },
        { label: "Land Preparation Methods", name: "land_preparation_methods" },
        {
          label: "Fertilizer & Nutrient Requirement",
          name: "fertilizer_nutrient_requirement",
        },
        { label: "Irrigation", name: "irrigation" },
        { label: "Harvesting Time", name: "harvesting_time" },
        { label: "Average Yield (tons/ha)", name: "average_yield_tons_per_ha" },
      ].map((field) => (
        <Grid item xs={12} md={6} key={field.name}>
          <TextField
            label={field.label}
            name={field.name}
            value={formData[field.name]}
            onChange={handleChange}
            fullWidth
            multiline={
              field.name.includes("requirement") ||
              field.name.includes("methods")
            }
            minRows={field.name.includes("requirement") ? 2 : 1}
          />
        </Grid>
      ))}

      {/* 🖼️ Image Upload Section */}
      <Grid item xs={12} sx={{ mt: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Crop Image
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            p: 2,
            border: "1px dashed #ccc",
            borderRadius: "12px",
            textAlign: "center",
            // backgroundColor: "#fafafa",
          }}
        >
          <Button
            variant="contained"
            component="label"
            sx={{
              textTransform: "none",
              mb: 1,
              borderRadius: "8px",
              px: 3,
              py: 1,
              fontWeight: 500,
            }}
          >
            {formData.image ? "Change Image" : "Upload Image"}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const previewUrl = URL.createObjectURL(file);
                  setFormData((prev) => ({
                    ...prev,
                    image: file,
                    imagePreview: previewUrl,
                  }));
                }
              }}
            />
          </Button>

          {formData.image && (
            <Button
              color="error"
              variant="outlined"
              size="small"
              sx={{ ml: 1, borderRadius: "8px" }}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  image: "",
                  imagePreview: "",
                }))
              }
            >
              Remove
            </Button>
          )}

          {formData.imagePreview && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img
                src={formData.imagePreview}
                alt="Preview"
                style={{
                  width: "100%",
                  maxWidth: "260px",
                  height: "auto",
                  borderRadius: "12px",
                  border: "1px solid #ccc",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                }}
              />
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  </DialogContent>

  {/* === Actions === */}
  <DialogActions sx={{ px: 3, pb: 2 }}>
    <Button onClick={onClose} disabled={saving}>
      Cancel
    </Button>
    <Button
      variant="contained"
      onClick={handleSubmit}
      disabled={saving}
      startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}
      sx={{ textTransform: "none", minWidth: 130, borderRadius: 1.5 }}
    >
      {saving ? "Saving..." : "Add Crop"}
    </Button>
  </DialogActions>
</Dialog>

  );
};

export default CropHubAddUpdate;
